import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatSelect, MatSelectChange } from "@angular/material/select";
import { FieldType, FieldTypeConfig } from "@ngx-formly/core";
import { CaseType } from "@vp/models";
import { UiDisplayTagService } from "@vp/shared/store/ui";
import { Subject } from "rxjs";
import { map, take } from "rxjs/operators";
import { CaseTypeSelectorService } from "../case-type-selector.service";

/**
 * This component is almost identical to the non-formly component in the root of this library
 * however due to the need to extend the FieldType is requires that it be its own component.
 * so they will share observables and nothing else.
 */
@Component({
  selector: "vp-formly-case-type-selector-type",
  standalone: false,
  templateUrl: "./formly-case-type-selector-type.component.html",
  styleUrls: ["./formly-case-type-selector-type.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CaseTypeSelectorService]
})
export class FormlyCaseTypeSelectorTypeComponent
  extends FieldType<FieldTypeConfig>
  implements OnInit, OnDestroy
{
  label = "Select Case Type";
  noneSelectedText = "All";
  defaultCaseTypeId: string | null = null;
  disabled = false;
  hideSelector = false;
  hideDescription = false;

  @ViewChild("caseTypeSelect") caseTypeSelect: MatSelect | undefined;

  private destroyed$ = new Subject<void>();

  constructor(
    public uiDisplayTagService: UiDisplayTagService,
    private readonly caseTypeSelectorService: CaseTypeSelectorService
  ) {
    super();
  }

  caseTypes$ = this.caseTypeSelectorService.caseTypes$;
  selectedCaseType$ = this.caseTypeSelectorService.selectedCaseType$;

  ngOnInit(): void {
    if (this.props?.label) {
      this.label = this.props?.label;
    }

    if (this.props?.noneSelectedText) {
      this.noneSelectedText = this.props.noneSelectedText;
    }

    if (this.props?.hideDescription) {
      this.hideDescription = this.props.hideDescription;
    }

    if (this.props?.defaultCaseTypeId) {
      this.defaultCaseTypeId = this.props.defaultCaseTypeId;
    }

    if (this.props?.hideSelector) {
      this.hideSelector = this.props.hideSelector;
    }

    this.caseTypeSelectorService.caseTypes$
      .pipe(
        map((caseTypes: CaseType[]) => {
          if (!this.defaultCaseTypeId && this.props.defaultCaseTypeId) {
            const caseType = caseTypes.find(ct => ct.caseTypeId === this.defaultCaseTypeId);
            if (caseType) {
              this.defaultCaseTypeId = this.props.defaultCaseTypeId;
              this.caseTypeSelectorService.setSelected(caseType.caseTypeId);
            }
          } else if (caseTypes.length === 1) {
            this.caseTypeSelectorService.setSelected(caseTypes[0].caseTypeId);
          }
        }),
        take(1)
      )
      .subscribe();

    this.caseTypeSelectorService.selectedCaseType$.pipe(take(1)).subscribe(caseType => {
      this.formControl.setValue(caseType?.caseTypeId);
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  selectionChanged($event: MatSelectChange) {
    this.caseTypeSelectorService.setSelected($event.value);
  }

  showDefaultOption(caseTypesCount: number) {
    return caseTypesCount > 1 && !this.hideDescription;
  }
}
